import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Admin.css";
import config from "./config";
import { useAuth } from "./contexts/AuthContext";
import Layout from "./components/Layout";
import AdminMcpIcon from "./components/AdminMcpIcon";
import EditMcpServerModal from "./components/EditMcpServerModal";
import McpSharingFields, { mcpVisibilityLabel } from "./components/McpSharingFields";
import ModelRankList from "./components/ModelRankList";
import ClaudeAgentsTab from "./admin/ClaudeAgents/ClaudeAgentsTab";

// Define initial state for a new function form
const newDefinitionPlaceholder = {
  type: "function",
  name: "get_users_wfh_by_date",
  description:
    "Gets a list of all employees who are marked as Working From Home (WFH) for a specific date. Use this when asked 'who is working from home on [specific date]?', 'who is remote next Friday?', etc.",
  parameters: {
    type: "object",
    properties: {
      date: {
        type: "string",
        description: "The date to check in YYYY-MM-DD format.",
      },
    },
    required: ["date"],
    additionalProperties: false,
  },
};

const initialNewFunctionFormState = {
  name: "", // Admin-facing display name
  description: "", // Admin-facing description
  n8n_webhook_url: "", // New field for n8n webhook URL
  definitionString: JSON.stringify(newDefinitionPlaceholder, null, 2),
  uiParameters: [
    {
      id: Date.now(),
      name: "",
      type: "string",
      description: "",
      required: false,
    },
  ],
};

// Define available parameter types for the dropdown
const functionParameterTypes = [
  { label: "String", value: "string" },
  { label: "Integer", value: "integer" },
  { label: "Number", value: "number" },
  { label: "Boolean", value: "boolean" },
  { label: "Array", value: "array" },
  { label: "Object", value: "object" },
];

const Admin = () => {
  const navigate = useNavigate();
  const { isCheckingAdmin, user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [modelsByProvider, setModelsByProvider] = useState({});
  const [loading, setLoading] = useState(true);
  const [modelLoading, setModelLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modelError, setModelError] = useState(null);
  const [toastMessage, setToastMessage] = useState({ text: "", type: "" });
  const showToast = useCallback(
    (text, type) => setToastMessage({ text, type }),
    []
  );

  // State for new admin selection UI
  const [tpUsers, setTpUsers] = useState([]);
  const [fetchingTpUsers, setFetchingTpUsers] = useState(true);
  const [tpUsersError, setTpUsersError] = useState(null);
  const [adminSearchQuery, setAdminSearchQuery] = useState("");
  const [adminSearchResults, setAdminSearchResults] = useState([]);
  const [selectedAdminsForSubmission, setSelectedAdminsForSubmission] =
    useState([]);

  // State for Custom Functions
  const [customFunctions, setCustomFunctions] = useState([]);
  const [loadingCustomFunctions, setLoadingCustomFunctions] = useState(true);
  const [customFunctionsError, setCustomFunctionsError] = useState(null);
  const [newFunctionForm, setNewFunctionForm] = useState(
    initialNewFunctionFormState
  );
  const [newFunctionError, setNewFunctionError] = useState(null);
  const [mcpServers, setMcpServers] = useState([]);
  const [loadingMcpServers, setLoadingMcpServers] = useState(true);
  const [mcpServersError, setMcpServersError] = useState(null);
  const [mcpIconVersion, setMcpIconVersion] = useState({});
  const [editingMcpServer, setEditingMcpServer] = useState(null);
  const [newMcpServer, setNewMcpServer] = useState({
    name: "",
    url: "",
    description: "",
    enabled: true,
    oauth_client_id: "",
    oauth_client_secret: "",
    oauth_scopes: "",
    oauth_authorize_endpoint: "",
    oauth_token_endpoint: "",
    shared_with_everyone: true,
    shared_with_users: [],
    shared_with_roles: [],
  });

  const [newAdmin, setNewAdmin] = useState({
    email: "",
    first_name: "",
    last_name: "",
  });
  const [newModel, setNewModel] = useState({
    name: "",
    id: "",
    reasoning: false,
    provider: "openai", // Default to OpenAI
    cost_per_million_input_tokens: "",
    cost_per_million_output_tokens: "",
    public: true,
    showOnGeoDashboard: false,
  });
  const [activeProviderTab, setActiveProviderTab] = useState("");

  // Provider options
  const providerOptions = [
    { label: "OpenAI", value: "openai" },
    { label: "Google", value: "google" },
    { label: "Anthropic", value: "anthropic" },
    { label: "Perplexity", value: "perplexity" },
  ];

  // Map provider value to label for display
  const getProviderLabel = (providerValue) => {
    const provider = providerOptions.find((opt) => opt.value === providerValue);
    return provider
      ? provider.label
      : providerValue.charAt(0).toUpperCase() + providerValue.slice(1);
  };

  const fetchTpUsers = useCallback(async () => {
    setFetchingTpUsers(true);
    setTpUsersError(null);
    try {
      const response = await fetch(
        `${config.api.baseUrl}/dashboard/active-tp-users`,
        { credentials: "include" } // Assuming cookies might be needed for future auth on this
      );
      const data = await response.json();
      if (data.success) {
        setTpUsers(data.data || []);
      } else {
        throw new Error(data.error || "Failed to fetch TargetProcess users");
      }
    } catch (err) {
      setTpUsersError(err.message);
      setTpUsers([]);
    } finally {
      setFetchingTpUsers(false);
    }
  }, []);

  // Fetch all admins on component mount
  useEffect(() => {
    // Only fetch admins if we're not still checking admin status
    if (!isCheckingAdmin) {
      fetchAdmins();
      fetchModels();
      fetchTpUsers();
      fetchCustomFunctions(); // Fetch custom functions
      fetchMcpServers();
    }
  }, [isCheckingAdmin, fetchTpUsers]);

  // Effect to clear toast message after a delay
  useEffect(() => {
    if (toastMessage.text) {
      const timer = setTimeout(() => {
        setToastMessage({ text: "", type: "" });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // Fetch admins from API
  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.admin.admins}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      const data = await response.json();

      if (data.success) {
        setAdmins(data.data || []);
        setError(null);
      } else {
        setError(data.error || "Failed to fetch admins");
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
      setError("An error occurred while fetching admins. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch models from API
  const fetchModels = async () => {
    try {
      setModelLoading(true);
      const response = await fetch(
        `${config.api.baseUrl}/admin/models/grouped`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      const data = await response.json();

      if (data.success && typeof data.models === "object") {
        setModelsByProvider(data.models || {});
        setModelError(null);
        // Keep current tab if it still exists; otherwise choose a default provider tab.
        const providerKeys = Object.keys(data.models || {});
        setActiveProviderTab((currentTab) => {
          if (providerKeys.length === 0) return "";
          if (currentTab && providerKeys.includes(currentTab)) return currentTab;

          const preferredOrder = [
            "openai",
            "google",
            "anthropic",
            "perplexity",
            "claude-agent",
          ];
          for (const preferred of preferredOrder) {
            if (providerKeys.includes(preferred)) return preferred;
          }
          return providerKeys[0];
        });
      } else {
        setModelsByProvider({});
        setModelError(data.error || "Failed to fetch models");
      }
    } catch (error) {
      console.error("Error fetching models:", error);
      setModelError(
        "An error occurred while fetching models. Please try again."
      );
    } finally {
      setModelLoading(false);
    }
  };

  // Fetch Custom Functions
  const fetchCustomFunctions = async () => {
    setLoadingCustomFunctions(true);
    setCustomFunctionsError(null);
    try {
      // Using the existing GET /chatbot/functions endpoint
      const response = await fetch(`${config.api.baseUrl}/chatbot/functions`, {
        method: "GET",
        credentials: "include", // Assuming this might be needed if auth evolves
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.functions)) {
        setCustomFunctions(data.functions);
      } else {
        throw new Error(data.error || "Failed to fetch custom functions");
      }
    } catch (err) {
      console.error("Error fetching custom functions:", err);
      setCustomFunctionsError(
        err.message || "An error occurred while fetching functions."
      );
      setCustomFunctions([]);
    } finally {
      setLoadingCustomFunctions(false);
    }
  };

  const fetchMcpServers = async () => {
    setLoadingMcpServers(true);
    setMcpServersError(null);
    try {
      const response = await fetch(`${config.api.baseUrl}/admin/mcp-servers`, {
        method: "GET",
        credentials: "include",
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setMcpServers(data.data);
      } else {
        throw new Error(data.error || "Failed to fetch MCP servers");
      }
    } catch (err) {
      setMcpServersError(err.message || "Failed to fetch MCP servers");
      setMcpServers([]);
    } finally {
      setLoadingMcpServers(false);
    }
  };

  // Handle input change for new admin form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAdmin({
      ...newAdmin,
      [name]: value,
    });
  };

  // Handle input change for new model form
  const handleModelInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox differently
    const newValue = type === "checkbox" ? checked : value;

    setNewModel({
      ...newModel,
      [name]: newValue,
    });
  };

  // Handle changes in the new function form (name, description, definitionString)
  const handleNewFunctionFormChange = (e) => {
    const { name, value } = e.target;
    setNewFunctionForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handle changes to a specific UI parameter row
  const handleUiParameterChange = (id, field, value) => {
    setNewFunctionForm((prev) => ({
      ...prev,
      uiParameters: prev.uiParameters.map((param) =>
        param.id === id ? { ...param, [field]: value } : param
      ),
    }));
  };

  // Add a new UI parameter row
  const addUiParameter = () => {
    setNewFunctionForm((prev) => ({
      ...prev,
      uiParameters: [
        ...prev.uiParameters,
        {
          id: Date.now(),
          name: "",
          type: "string",
          description: "",
          required: false,
        },
      ],
    }));
  };

  // Remove a UI parameter row by its ID
  const removeUiParameter = (id) => {
    setNewFunctionForm((prev) => ({
      ...prev,
      uiParameters: prev.uiParameters.filter((param) => param.id !== id),
    }));
  };

  // Handle form submission to add new admin
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newAdmin.email || !newAdmin.first_name || !newAdmin.last_name) {
      setError("Please fill all fields");
      return;
    }

    try {
      const response = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.admin.admins}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newAdmin),
          credentials: "include",
        }
      );

      const data = await response.json();

      if (data.success) {
        // Reset form and reload admins
        setNewAdmin({ email: "", first_name: "", last_name: "" });
        fetchAdmins();
      } else {
        setError(data.error || "Failed to add admin");
      }
    } catch (error) {
      console.error("Error adding admin:", error);
      setError("An error occurred while adding admin. Please try again.");
    }
  };

  // Handle form submission to add new model
  const handleModelSubmit = async (e) => {
    e.preventDefault();

    if (!newModel.name || !newModel.id) {
      setModelError("Please fill all required fields (Name and ID)");
      return;
    }

    try {
      const response = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.admin.models}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newModel),
          credentials: "include",
        }
      );

      const data = await response.json();

      if (data.success) {
        // Reset form and reload models
        setNewModel({
          name: "",
          id: "",
          reasoning: false,
          provider: "openai", // Default to OpenAI
          cost_per_million_input_tokens: "",
          cost_per_million_output_tokens: "",
          public: true,
          showOnGeoDashboard: false,
        });
        fetchModels();
      } else {
        setModelError(data.error || "Failed to add model");
      }
    } catch (error) {
      console.error("Error adding model:", error);
      setModelError("An error occurred while adding model. Please try again.");
    }
  };

  // Handle admin deletion
  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm("Are you sure you want to delete this admin?")) {
      return;
    }

    try {
      const response = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.admin.admins}/${adminId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      const data = await response.json();

      if (data.success) {
        fetchAdmins();
      } else {
        setError(data.error || "Failed to delete admin");
      }
    } catch (error) {
      console.error("Error deleting admin:", error);
      setError("An error occurred while deleting admin. Please try again.");
    }
  };

  // Handle model deletion
  const handleDeleteModel = async (modelId) => {
    if (!window.confirm("Are you sure you want to delete this model?")) {
      return;
    }

    try {
      const response = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.admin.models}/${modelId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      const data = await response.json();

      if (data.success) {
        fetchModels();
      } else {
        setModelError(data.error || "Failed to delete model");
      }
    } catch (error) {
      console.error("Error deleting model:", error);
      setModelError(
        "An error occurred while deleting model. Please try again."
      );
    }
  };

  // Handle submission of the new function form
  const handleAddNewFunction = async (e) => {
    e.preventDefault();
    setNewFunctionError(null);

    if (
      !newFunctionForm.name.trim() ||
      !newFunctionForm.definitionString.trim() ||
      !newFunctionForm.n8n_webhook_url.trim() // Added check for n8n URL
    ) {
      setNewFunctionError(
        "Admin Display Name, n8n Webhook URL, and Function Definition (JSON) are required."
      );
      return;
    }

    let parsedDefinition;
    try {
      parsedDefinition = JSON.parse(newFunctionForm.definitionString);
    } catch (jsonError) {
      setNewFunctionError(
        "Invalid JSON in Function Definition: " + jsonError.message
      );
      return;
    }

    if (!parsedDefinition.parameters) {
      parsedDefinition.parameters = {
        type: "object",
        properties: {},
        required: [],
      };
    } else {
      if (
        typeof parsedDefinition.parameters !== "object" ||
        parsedDefinition.parameters === null
      ) {
        setNewFunctionError(
          "Function Definition 'parameters' must be an object."
        );
        return;
      }
      if (!parsedDefinition.parameters.properties) {
        parsedDefinition.parameters.properties = {};
      }
      if (!parsedDefinition.parameters.required) {
        parsedDefinition.parameters.required = [];
      }
    }

    const properties = {};
    const requiredParams = [];

    for (const uiParam of newFunctionForm.uiParameters) {
      if (uiParam.name.trim()) {
        properties[uiParam.name.trim()] = {
          type: uiParam.type,
          description: uiParam.description.trim(),
        };
        if (uiParam.required) {
          requiredParams.push(uiParam.name.trim());
        }
      }
    }

    if (
      newFunctionForm.uiParameters.length > 0 &&
      newFunctionForm.uiParameters.some((p) => p.name.trim())
    ) {
      parsedDefinition.parameters.properties = properties;
      parsedDefinition.parameters.required = requiredParams;
    }

    if (
      !parsedDefinition.name ||
      typeof parsedDefinition.name !== "string" ||
      !parsedDefinition.name.trim()
    ) {
      setNewFunctionError(
        "The 'name' property within the Function Definition JSON is required and must be a non-empty string."
      );
      return;
    }

    try {
      const response = await fetch(`${config.api.baseUrl}/admin/functions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newFunctionForm.name.trim(),
          description: newFunctionForm.description.trim(),
          n8n_webhook_url: newFunctionForm.n8n_webhook_url.trim(),
          definition: parsedDefinition,
        }),
        credentials: "include",
      });
      const data = await response.json();
      if (data.success) {
        setNewFunctionForm(initialNewFunctionFormState);
        fetchCustomFunctions();
        setToastMessage({
          text: "Custom function added successfully!",
          type: "success",
        });
        setNewFunctionError(null);
      } else {
        setNewFunctionError(data.error || "Failed to add custom function");
        setToastMessage({
          text: data.error || "Failed to add custom function",
          type: "error",
        });
      }
    } catch (err) {
      console.error("Error adding custom function:", err);
      setNewFunctionError("An error occurred. Please try again.");
      setToastMessage({
        text: "An error occurred while adding function.",
        type: "error",
      });
    }
  };

  const handleMcpServerInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewMcpServer((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCreateMcpServer = async (e) => {
    e.preventDefault();
    setMcpServersError(null);
    if (!newMcpServer.name.trim() || !newMcpServer.url.trim()) {
      setMcpServersError("MCP name and URL are required.");
      return;
    }
    try {
      const response = await fetch(`${config.api.baseUrl}/admin/mcp-servers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newMcpServer),
      });
      const data = await response.json();
      if (data.success) {
        setNewMcpServer({
          name: "", url: "", description: "", enabled: true,
          oauth_client_id: "", oauth_client_secret: "", oauth_scopes: "",
          oauth_authorize_endpoint: "", oauth_token_endpoint: "",
          shared_with_everyone: true, shared_with_users: [], shared_with_roles: [],
        });
        fetchMcpServers();
        showToast("MCP server added", "success");
      } else {
        setMcpServersError(data.error || "Failed to add MCP server");
      }
    } catch (err) {
      setMcpServersError("An error occurred while adding MCP server.");
    }
  };

  const handleDeleteMcpServer = async (serverId) => {
    if (!window.confirm("Delete this MCP server?")) return;
    try {
      const response = await fetch(
        `${config.api.baseUrl}/admin/mcp-servers/${serverId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      const data = await response.json();
      if (data.success) {
        fetchMcpServers();
        showToast("MCP server deleted", "success");
      } else {
        setMcpServersError(data.error || "Failed to delete MCP server");
      }
    } catch (err) {
      setMcpServersError("An error occurred while deleting MCP server.");
    }
  };

  const handleToggleMcpServerEnabled = async (server) => {
    try {
      const response = await fetch(
        `${config.api.baseUrl}/admin/mcp-servers/${server._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ enabled: !server.enabled }),
        }
      );
      const data = await response.json();
      if (data.success) {
        fetchMcpServers();
      } else {
        setMcpServersError(data.error || "Failed to update MCP server");
      }
    } catch (err) {
      setMcpServersError("An error occurred while updating MCP server.");
    }
  };

  // Manual MCP icon override. `mcpIconVersion` busts the <img> cache after an
  // upload/remove (the icon endpoint is cached per URL).
  const handleUploadMcpIcon = async (server, file) => {
    if (!file) return;
    setMcpServersError(null);
    try {
      const form = new FormData();
      form.append("icon", file);
      const response = await fetch(
        `${config.api.baseUrl}/admin/mcp-servers/${server._id}/icon`,
        { method: "POST", credentials: "include", body: form }
      );
      const data = await response.json();
      if (data.success) {
        setMcpIconVersion((v) => ({ ...v, [server._id]: (v[server._id] || 0) + 1 }));
        showToast("Icon updated", "success");
      } else {
        setMcpServersError(data.error || "Failed to upload icon");
      }
    } catch (err) {
      setMcpServersError("An error occurred while uploading the icon.");
    }
  };

  const handleRemoveMcpIcon = async (server) => {
    setMcpServersError(null);
    try {
      const response = await fetch(
        `${config.api.baseUrl}/admin/mcp-servers/${server._id}/icon`,
        { method: "DELETE", credentials: "include" }
      );
      const data = await response.json();
      if (data.success) {
        setMcpIconVersion((v) => ({ ...v, [server._id]: (v[server._id] || 0) + 1 }));
        showToast("Custom icon removed", "success");
      } else {
        setMcpServersError(data.error || "Failed to remove icon");
      }
    } catch (err) {
      setMcpServersError("An error occurred while removing the icon.");
    }
  };

  // Handle Custom Function Deletion
  const handleDeleteCustomFunction = async (functionId) => {
    if (!window.confirm("Are you sure you want to delete this function?")) {
      return;
    }
    try {
      const response = await fetch(
        `${config.api.baseUrl}/admin/functions/${functionId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      const data = await response.json();
      if (data.success) {
        fetchCustomFunctions(); // Refresh the list
        setToastMessage({
          text: "Function deleted successfully!",
          type: "success",
        });
      } else {
        setCustomFunctionsError(data.error || "Failed to delete function");
        setToastMessage({
          text: data.error || "Failed to delete function",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting function:", error);
      setCustomFunctionsError(
        "An error occurred while deleting function. Please try again."
      );
      setToastMessage({
        text: "An error occurred while deleting function.",
        type: "error",
      });
    }
  };

  // Memoized sorted provider keys for tabs
  const sortedProviderKeys = useMemo(() => {
    const keys = Object.keys(modelsByProvider);
    const preferredOrder = [
      "openai",
      "google",
      "anthropic",
      "perplexity",
      "claude-agent",
    ];
    return [
      ...preferredOrder.filter((key) => keys.includes(key)),
      ...keys.filter((key) => !preferredOrder.includes(key)).sort(),
    ];
  }, [modelsByProvider]);

  const currentModelsToList = modelsByProvider[activeProviderTab] || [];

  const handleModelUpdatedInState = useCallback((updatedModel) => {
    if (!updatedModel || !updatedModel._id) return;

    setModelsByProvider((prev) => {
      const next = {};
      Object.keys(prev).forEach((providerKey) => {
        next[providerKey] = (prev[providerKey] || []).map((model) =>
          model._id === updatedModel._id ? updatedModel : model
        );
      });
      return next;
    });
  }, []);

  const handleReorderModels = useCallback(
    async (reordered) => {
      const provider = activeProviderTab;
      const before = modelsByProvider[provider] || [];
      setModelsByProvider((prev) => ({ ...prev, [provider]: reordered }));
      try {
        const response = await fetch(
          `${config.api.baseUrl}${config.api.endpoints.admin.modelsReorder}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              provider,
              ordered_ids: reordered.map((m) => m.id),
            }),
          }
        );
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || "Failed to save model order");
        }
        showToast("Model order saved", "success");
      } catch (err) {
        setModelsByProvider((prev) => ({ ...prev, [provider]: before }));
        showToast(err.message || "Failed to save model order", "error");
      }
    },
    [activeProviderTab, modelsByProvider, showToast]
  );

  const handleAdminSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setAdminSearchQuery(query);
    if (query.length > 1) {
      // Start searching after 2 characters
      const filtered = tpUsers
        .filter(
          (user) =>
            user.email.toLowerCase().includes(query) ||
            user.firstName.toLowerCase().includes(query) ||
            user.lastName.toLowerCase().includes(query)
        )
        .slice(0, 10); // Limit to 10 results for performance
      setAdminSearchResults(filtered);
    } else {
      setAdminSearchResults([]);
    }
  };

  const handleSelectAdminFromSearch = (tpUser) => {
    if (
      !selectedAdminsForSubmission.find((admin) => admin.email === tpUser.email)
    ) {
      setSelectedAdminsForSubmission((prev) => [...prev, tpUser]);
    }
    setAdminSearchQuery("");
    setAdminSearchResults([]);
  };

  const handleRemoveSelectedAdmin = (email) => {
    setSelectedAdminsForSubmission((prev) =>
      prev.filter((admin) => admin.email !== email)
    );
  };

  // Updated to handle submission of selected admins
  const handleSubmitAdmins = async (e) => {
    e.preventDefault();
    if (selectedAdminsForSubmission.length === 0) {
      setError("Please select at least one user to add as admin.");
      return;
    }
    setError(null); // Clear previous errors

    // Prepare data in the format expected by the backend (firstName, lastName, email, avatarUri)
    const adminsToSubmit = selectedAdminsForSubmission.map((user) => ({
      email: user.email,
      first_name: user.firstName, // Match backend expectation
      last_name: user.lastName, // Match backend expectation
      avatarUri: user.avatarUri,
    }));

    try {
      const response = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.admin.admins}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(adminsToSubmit), // Send the array
          credentials: "include",
        }
      );
      const data = await response.json();
      if (data.success) {
        setSelectedAdminsForSubmission([]); // Clear selection
        fetchAdmins(); // Refresh admin list
        setToastMessage({
          text: data.message || "Admins processed successfully!",
          type: "success",
        });
      } else {
        setToastMessage({
          text: data.error || data.message || "Failed to add admins",
          type: "error",
        });
      }
    } catch (err) {
      console.error("Error adding admins:", err);
      setToastMessage({
        text: "An error occurred while adding admins. Please try again.",
        type: "error",
      });
    }
  };

  return (
    <Layout>
      <div className="admin-container">
        <div className="admin-header">
          <h1>Admin Dashboard</h1>
          <p className="admin-description">
            Manage users and AI models for your organization
          </p>
        </div>

        {/* Toast Message Display */}
        {toastMessage.text && (
          <div className={`admin-toast-message ${toastMessage.type}`}>
            {toastMessage.text}
          </div>
        )}

        {/* Admin Section */}
        <div className="admin-section">
          <div className="section-header">
            <h2>Admin Management</h2>
            <div className="section-divider"></div>
          </div>

          {/* Error message display for form validation or TP user fetch errors */}
          {error && <div className="error-message">{error}</div>}
          {tpUsersError && (
            <div className="error-message">
              Failed to load users: {tpUsersError}
            </div>
          )}

          <div className="admin-grid">
            {/* Add new admin form */}
            <div className="admin-subsection form-panel">
              <h3>Add New Admins</h3>
              <form
                onSubmit={handleSubmitAdmins}
                className="admin-form admin-search-form"
              >
                <div className="form-group admin-search-group">
                  <label htmlFor="adminSearch">
                    Search Users (Name or Email)
                  </label>
                  <input
                    type="text"
                    id="adminSearch"
                    name="adminSearch"
                    value={adminSearchQuery}
                    onChange={handleAdminSearchChange}
                    placeholder="Start typing to search..."
                    autoComplete="off"
                  />
                  {adminSearchResults.length > 0 && (
                    <ul className="admin-search-results">
                      {adminSearchResults.map((user) => (
                        <li
                          key={user.email}
                          onClick={() => handleSelectAdminFromSearch(user)}
                        >
                          <img
                            src={
                              user.avatarUri
                                ? `${config.api.baseUrl}${
                                    config.api.endpoints.dashboard.tpAvatar
                                  }?url=${encodeURIComponent(user.avatarUri)}`
                                : "./img/default-avatar.png"
                            }
                            alt={`${user.firstName} ${user.lastName}`}
                            className="search-avatar"
                          />
                          <span>
                            {user.firstName} {user.lastName} ({user.email})
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {selectedAdminsForSubmission.length > 0 && (
                  <div className="selected-admins-list">
                    <h4>Selected for Admin Access:</h4>
                    <ul>
                      {selectedAdminsForSubmission.map((user) => (
                        <li key={user.email}>
                          <img
                            src={
                              user.avatarUri
                                ? `${config.api.baseUrl}${
                                    config.api.endpoints.dashboard.tpAvatar
                                  }?url=${encodeURIComponent(user.avatarUri)}`
                                : "./img/default-avatar.png"
                            }
                            alt={`${user.firstName} ${user.lastName}`}
                            className="selected-avatar"
                          />
                          <span>
                            {user.firstName} {user.lastName} ({user.email})
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveSelectedAdmin(user.email)
                            }
                            className="remove-selected-btn"
                            title="Remove"
                          >
                            &times;
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <button
                  type="submit"
                  className="admin-submit-button"
                  disabled={
                    selectedAdminsForSubmission.length === 0 || fetchingTpUsers
                  }
                >
                  Add Selected Admins
                </button>
              </form>
            </div>

            {/* List of admins */}
            <div className="admin-subsection list-panel">
              <h3>Admin Users</h3>
              {isCheckingAdmin ? (
                <div className="loading">Verifying admin access...</div>
              ) : loading ? (
                <div className="loading">Loading admins...</div>
              ) : admins.length > 0 ? (
                <div className="table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Avatar</th>
                        <th>Email</th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {admins.map((admin) => (
                        <tr key={admin._id}>
                          <td>
                            {admin.avatarUri ? (
                              <img
                                src={`${config.api.baseUrl}${
                                  config.api.endpoints.dashboard.tpAvatar
                                }?url=${encodeURIComponent(admin.avatarUri)}`}
                                alt={`${admin.first_name} ${admin.last_name}`}
                                className="admin-list-avatar"
                              />
                            ) : (
                              <div className="admin-list-avatar-placeholder">
                                {admin.first_name?.[0]}
                                {admin.last_name?.[0]}
                              </div>
                            )}
                          </td>
                          <td>{admin.email}</td>
                          <td>{admin.first_name}</td>
                          <td>{admin.last_name}</td>
                          <td>
                            <button
                              className="delete-button"
                              onClick={() => handleDeleteAdmin(admin._id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-data">No admins found</div>
              )}
            </div>
          </div>
        </div>

        {/* Model Management Section */}
        <div className="admin-section">
          <div className="section-header">
            <h2>Model Management</h2>
            <div className="section-divider"></div>
          </div>

          {/* Error message display */}
          {modelError && <div className="error-message">{modelError}</div>}

          <div className="admin-grid">
            {/* Add new model form */}
            <div className="admin-subsection form-panel">
              <h3>Add New Model</h3>
              <form onSubmit={handleModelSubmit} className="admin-form">
                <div className="form-group">
                  <label htmlFor="name">
                    Display Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={newModel.name}
                    onChange={handleModelInputChange}
                    required
                    placeholder="e.g. GPT-4-Turbo"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="id">
                    Model ID <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="id"
                    name="id"
                    value={newModel.id}
                    onChange={handleModelInputChange}
                    required
                    placeholder="e.g. gpt-4-turbo"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="provider">
                    Provider <span className="required">*</span>
                  </label>
                  <select
                    id="provider"
                    name="provider"
                    value={newModel.provider}
                    onChange={handleModelInputChange}
                    required
                    className="provider-select"
                  >
                    {providerOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="cost_per_million_input_tokens">
                    Cost per 1M Input Tokens
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    min="0"
                    id="cost_per_million_input_tokens"
                    name="cost_per_million_input_tokens"
                    value={newModel.cost_per_million_input_tokens}
                    onChange={handleModelInputChange}
                    placeholder="e.g. 5.00"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="cost_per_million_output_tokens">
                    Cost per 1M Output Tokens
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    min="0"
                    id="cost_per_million_output_tokens"
                    name="cost_per_million_output_tokens"
                    value={newModel.cost_per_million_output_tokens}
                    onChange={handleModelInputChange}
                    placeholder="e.g. 15.00"
                  />
                </div>

                <div className="form-group-checkbox">
                  <input
                    type="checkbox"
                    id="public"
                    name="public"
                    checked={!newModel.public}
                    onChange={(e) =>
                      setNewModel((prev) => ({
                        ...prev,
                        public: !e.target.checked,
                      }))
                    }
                  />
                  <label htmlFor="public">
                    Private (hide from chatbot selection)
                  </label>
                </div>

                <div className="form-group-checkbox">
                  <input
                    type="checkbox"
                    id="reasoning"
                    name="reasoning"
                    checked={newModel.reasoning}
                    onChange={handleModelInputChange}
                  />
                  <label htmlFor="reasoning">Supports reasoning</label>
                </div>

                <div className="form-group-checkbox">
                  <input
                    type="checkbox"
                    id="showOnGeoDashboard"
                    name="showOnGeoDashboard"
                    checked={!!newModel.showOnGeoDashboard}
                    onChange={handleModelInputChange}
                  />
                  <label htmlFor="showOnGeoDashboard">
                    Show on GEO dashboard
                  </label>
                </div>

                <button type="submit" className="admin-submit-button">
                  Add Model
                </button>
              </form>
            </div>

            {/* List of models with Tabs */}
            <div className="admin-subsection list-panel models-list-panel">
              <h3>Available Models</h3>
              {modelLoading ? (
                <div className="loading">Loading models...</div>
              ) : sortedProviderKeys.length > 0 ? (
                <>
                  <div className="provider-tabs-container">
                    {sortedProviderKeys.map((providerKey) => (
                      <button
                        key={providerKey}
                        className={`provider-tab ${
                          activeProviderTab === providerKey ? "active" : ""
                        }`}
                        onClick={() => setActiveProviderTab(providerKey)}
                      >
                        {getProviderLabel(providerKey)} (
                        {modelsByProvider[providerKey]?.length || 0})
                      </button>
                    ))}
                  </div>
                  {/* "Unknown" is a synthetic bucket for provider-less docs,
                      not a real provider — every reorder PUT for it 400s. */}
                  {currentModelsToList.length > 1 &&
                    activeProviderTab !== "Unknown" && (
                    <div className="model-rank-panel" style={{ margin: "16px 0" }}>
                      <h4 style={{ margin: "0 0 4px" }}>Model ranking</h4>
                      <p style={{ opacity: 0.6, fontSize: 13, margin: "0 0 8px" }}>
                        Drag to reorder. Dropdowns list models in this order; the
                        top model is the default for new chatbots and the fallback
                        when a chatbot's model is deleted. Saved automatically.
                      </p>
                      <ModelRankList
                        items={currentModelsToList}
                        getKey={(m) => m.id}
                        renderItem={(m) => (
                          <span>
                            {m.name}{" "}
                            <code style={{ opacity: 0.5, fontSize: 12 }}>{m.id}</code>
                          </span>
                        )}
                        onReorder={handleReorderModels}
                      />
                    </div>
                  )}
                  <div className="model-grid">
                    {currentModelsToList.map((model) => (
                      <ModelCard
                        key={model._id}
                        model={model}
                        onDelete={() => handleDeleteModel(model._id)}
                        onSaved={fetchModels}
                        onModelUpdated={handleModelUpdatedInState}
                        getProviderLabel={getProviderLabel}
                        onToast={showToast}
                      />
                    ))}
                  </div>
                  {currentModelsToList.length === 0 && activeProviderTab && (
                    <div className="no-data">
                      No models found for {getProviderLabel(activeProviderTab)}.
                    </div>
                  )}
                </>
              ) : (
                <div className="no-data">
                  No models found. Add a model to get started.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Custom Function Management Section */}
        <div className="admin-section">
          <div className="section-header">
            <h2>Function Management</h2>
            <div className="section-divider"></div>
          </div>
          {/* Error display for new function form */}
          {newFunctionError && (
            <div className="error-message" style={{ marginBottom: "20px" }}>
              {newFunctionError}
            </div>
          )}
          {customFunctionsError && !newFunctionError && (
            <div className="error-message">{customFunctionsError}</div>
          )}
          <div className="admin-grid">
            {/* Add New Function Form will go here */}
            <div className="admin-subsection form-panel">
              <h3>Add New Function</h3>
              <form onSubmit={handleAddNewFunction} className="admin-form">
                <div className="form-group">
                  <label htmlFor="functionName">
                    Admin Display Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="functionName"
                    name="name"
                    value={newFunctionForm.name}
                    onChange={handleNewFunctionFormChange}
                    required
                    placeholder="e.g., Get User Details"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="functionDescription">
                    Admin Display Description
                  </label>
                  <textarea
                    id="functionDescription"
                    name="description"
                    value={newFunctionForm.description}
                    onChange={handleNewFunctionFormChange}
                    rows={3}
                    placeholder="Briefly describe what this function is for in the admin panel."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="n8nWebhookUrl">
                    n8n Webhook URL <span className="required">*</span>
                  </label>
                  <input
                    type="url"
                    id="n8nWebhookUrl"
                    name="n8n_webhook_url"
                    value={newFunctionForm.n8n_webhook_url}
                    onChange={handleNewFunctionFormChange}
                    placeholder="https://your-n8n-instance.com/webhook/..."
                    required // HTML5 required attribute
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="definitionString">
                    Function Definition (JSON for AI){" "}
                    <span className="required">*</span>
                  </label>
                  <textarea
                    id="definitionString"
                    name="definitionString"
                    value={newFunctionForm.definitionString}
                    onChange={handleNewFunctionFormChange}
                    required
                    placeholder={JSON.stringify(
                      newDefinitionPlaceholder,
                      null,
                      2
                    )} // Updated placeholder
                  />
                  {/* <small> The <code>name</code> inside this JSON must be unique. Parameters below will override <code>parameters.properties</code> and <code>parameters.required</code> in this JSON. </small> */}
                </div>

                <h4>Define UI-Managed Parameters:</h4>
                {newFunctionForm.uiParameters.map((param, index) => (
                  <div key={param.id} className="function-form-parameter-row">
                    <div className="form-group">
                      <label htmlFor={`param-name-${param.id}`}>
                        Param Name
                      </label>
                      <input
                        type="text"
                        id={`param-name-${param.id}`}
                        value={param.name}
                        onChange={(e) =>
                          handleUiParameterChange(
                            param.id,
                            "name",
                            e.target.value
                          )
                        }
                        placeholder="e.g., location"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor={`param-type-${param.id}`}>Type</label>
                      <select
                        id={`param-type-${param.id}`}
                        value={param.type}
                        onChange={(e) =>
                          handleUiParameterChange(
                            param.id,
                            "type",
                            e.target.value
                          )
                        }
                        className="parameter-type-select"
                      >
                        {functionParameterTypes.map((typeOpt) => (
                          <option key={typeOpt.value} value={typeOpt.value}>
                            {typeOpt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor={`param-desc-${param.id}`}>
                        Description
                      </label>
                      <input
                        type="text"
                        id={`param-desc-${param.id}`}
                        value={param.description}
                        onChange={(e) =>
                          handleUiParameterChange(
                            param.id,
                            "description",
                            e.target.value
                          )
                        }
                        placeholder="e.g., City and state"
                      />
                    </div>
                    <div className="form-group-checkbox parameter-required-checkbox">
                      <input
                        type="checkbox"
                        id={`param-req-${param.id}`}
                        checked={param.required}
                        onChange={(e) =>
                          handleUiParameterChange(
                            param.id,
                            "required",
                            e.target.checked
                          )
                        }
                      />
                      <label htmlFor={`param-req-${param.id}`}>Required</label>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeUiParameter(param.id)}
                      className="parameter-remove-button"
                      title="Remove Parameter"
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addUiParameter}
                  className="parameter-add-button"
                >
                  + Add Parameter
                </button>

                <button
                  type="submit"
                  className="admin-submit-button"
                  style={{ marginTop: "20px" }}
                >
                  Add Function
                </button>
              </form>
            </div>

            {/* List of Custom Functions */}
            <div className="admin-subsection list-panel functions-list-panel">
              <h3>Available Functions</h3>
              {loadingCustomFunctions ? (
                <div className="loading">Loading functions...</div>
              ) : customFunctions.length > 0 ? (
                <div className="functions-grid">
                  {customFunctions.map((func) => (
                    <div key={func._id} className="function-card">
                      <div className="function-card-header">
                        <h4>{func.name}</h4>
                        <button
                          className="delete-button small"
                          onClick={() => handleDeleteCustomFunction(func._id)}
                          title="Delete function"
                        >
                          &times;
                        </button>
                      </div>
                      <div className="function-card-body">
                        <p className="function-description">
                          {func.description || "No description provided."}
                        </p>
                        {func.n8n_webhook_url && (
                          <div className="function-n8n-url-container">
                            <strong>n8n Webhook URL:</strong>
                            <p className="function-n8n-url">
                              {func.n8n_webhook_url}
                            </p>
                          </div>
                        )}
                        {!func.n8n_webhook_url && (
                          <div className="function-n8n-url-container">
                            <p className="function-n8n-url no-url">
                              <em>No n8n webhook URL provided.</em>
                            </p>
                          </div>
                        )}
                        {func.definition && (
                          <details className="function-definition-details">
                            <summary>View Definition (JSON)</summary>
                            <pre className="function-definition-json">
                              {JSON.stringify(func.definition, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-data">
                  No custom functions found. Add a function to get started.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* MCP Server Management Section */}
        <div className="admin-section">
          <div className="section-header">
            <h2>MCP Server Management</h2>
            <div className="section-divider"></div>
          </div>
          {mcpServersError && <div className="error-message">{mcpServersError}</div>}
          <div className="admin-grid">
            <div className="admin-subsection form-panel">
              <h3>Add MCP Server</h3>
              <form onSubmit={handleCreateMcpServer} className="admin-form">
                <div className="form-group">
                  <label htmlFor="mcp_name">
                    Name <span className="required">*</span>
                  </label>
                  <input
                    id="mcp_name"
                    name="name"
                    value={newMcpServer.name}
                    onChange={handleMcpServerInputChange}
                    placeholder="e.g., Miro"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="mcp_url">
                    URL <span className="required">*</span>
                  </label>
                  <input
                    id="mcp_url"
                    name="url"
                    value={newMcpServer.url}
                    onChange={handleMcpServerInputChange}
                    placeholder="https://..."
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="mcp_description">Description</label>
                  <textarea
                    id="mcp_description"
                    name="description"
                    rows={3}
                    value={newMcpServer.description}
                    onChange={handleMcpServerInputChange}
                    placeholder="Optional description shown in UI"
                  />
                </div>
                <div className="form-group-checkbox">
                  <input
                    type="checkbox"
                    id="mcp_enabled"
                    name="enabled"
                    checked={newMcpServer.enabled}
                    onChange={handleMcpServerInputChange}
                  />
                  <label htmlFor="mcp_enabled">Enabled</label>
                </div>
                <McpSharingFields value={newMcpServer} onChange={setNewMcpServer} />
                <details className="form-group" style={{ marginTop: 8 }}>
                  <summary style={{ cursor: "pointer" }}>
                    OAuth (advanced) — for servers without auto-registration (e.g. Slack)
                  </summary>
                  <div className="form-group">
                    <label htmlFor="mcp_oauth_client_id">OAuth Client ID</label>
                    <input
                      id="mcp_oauth_client_id"
                      name="oauth_client_id"
                      value={newMcpServer.oauth_client_id}
                      onChange={handleMcpServerInputChange}
                      placeholder="From your Slack app → Basic Information"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="mcp_oauth_client_secret">OAuth Client Secret</label>
                    <input
                      id="mcp_oauth_client_secret"
                      name="oauth_client_secret"
                      type="password"
                      value={newMcpServer.oauth_client_secret}
                      onChange={handleMcpServerInputChange}
                      placeholder="Stored write-only; never shown after saving"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="mcp_oauth_scopes">OAuth Scopes (optional)</label>
                    <input
                      id="mcp_oauth_scopes"
                      name="oauth_scopes"
                      value={newMcpServer.oauth_scopes}
                      onChange={handleMcpServerInputChange}
                      placeholder="Space-separated; blank = auto-discover"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="mcp_oauth_authorize_endpoint">
                      Authorize Endpoint override (optional)
                    </label>
                    <input
                      id="mcp_oauth_authorize_endpoint"
                      name="oauth_authorize_endpoint"
                      value={newMcpServer.oauth_authorize_endpoint}
                      onChange={handleMcpServerInputChange}
                      placeholder="Leave blank to use discovery"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="mcp_oauth_token_endpoint">
                      Token Endpoint override (optional)
                    </label>
                    <input
                      id="mcp_oauth_token_endpoint"
                      name="oauth_token_endpoint"
                      value={newMcpServer.oauth_token_endpoint}
                      onChange={handleMcpServerInputChange}
                      placeholder="Leave blank to use discovery"
                    />
                  </div>
                </details>
                <button type="submit" className="admin-submit-button">
                  Add MCP Server
                </button>
              </form>
            </div>

            <div className="admin-subsection list-panel">
              <h3>Global MCP Servers</h3>
              {loadingMcpServers ? (
                <div className="loading">Loading MCP servers...</div>
              ) : mcpServers.length > 0 ? (
                <div className="table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Icon</th>
                        <th>Name</th>
                        <th>URL</th>
                        <th>Enabled</th>
                        <th>Visible to</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mcpServers.map((server) => (
                        <tr key={server._id}>
                          <td>
                            <AdminMcpIcon
                              url={server.url}
                              version={mcpIconVersion[server._id] || 0}
                            />
                          </td>
                          <td>{server.name}</td>
                          <td className="mcp-url-cell" title={server.url}>{server.url}</td>
                          <td>{server.enabled ? "Yes" : "No"}</td>
                          <td>{mcpVisibilityLabel(server)}</td>
                          <td>
                            <div className="mcp-actions">
                              <button
                                className="admin-submit-button"
                                onClick={() => setEditingMcpServer(server)}
                              >
                                Edit
                              </button>
                              <button
                                className="admin-submit-button"
                                onClick={() => handleToggleMcpServerEnabled(server)}
                              >
                                {server.enabled ? "Disable" : "Enable"}
                              </button>
                              <label className="admin-submit-button mcp-upload-label">
                                Upload Icon
                                <input
                                  type="file"
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  onChange={(e) => {
                                    handleUploadMcpIcon(server, e.target.files?.[0]);
                                    e.target.value = "";
                                  }}
                                />
                              </label>
                              <button
                                className="admin-submit-button"
                                onClick={() => handleRemoveMcpIcon(server)}
                              >
                                Reset Icon
                              </button>
                              <button
                                className="delete-button"
                                onClick={() => handleDeleteMcpServer(server._id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-data">No MCP servers configured</div>
              )}
            </div>
          </div>
        </div>

        {editingMcpServer && (
          <EditMcpServerModal
            key={editingMcpServer._id}
            server={editingMcpServer}
            onClose={() => setEditingMcpServer(null)}
            onSaved={() => {
              setEditingMcpServer(null);
              fetchMcpServers();
              showToast("MCP server updated", "success");
            }}
          />
        )}

        {/* Claude Agents Section */}
        <div className="admin-section">
          <div className="section-header">
            <h2>Claude Agents</h2>
            <div className="section-divider"></div>
          </div>
          <ClaudeAgentsTab />
        </div>

        {user?.role === "Top Manager" && (
          <div className="admin-section">
            <div className="section-header">
              <h2>Chatbot Usage</h2>
              <div className="section-divider"></div>
            </div>
            <button className="admin-submit-button" onClick={() => navigate("/chatbot-admin")}>
              Open chatbot usage dashboard
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

// Editable model card for cost fields
const ModelCard = ({
  model,
  onDelete,
  onSaved,
  onModelUpdated,
  getProviderLabel,
  onToast,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [geoToggleSaving, setGeoToggleSaving] = useState(false);
  const [formValues, setFormValues] = useState({
    cost_per_million_input_tokens: model.cost_per_million_input_tokens ?? "",
    cost_per_million_output_tokens: model.cost_per_million_output_tokens ?? "",
    public: model.public !== undefined ? !!model.public : true,
    showOnGeoDashboard:
      model.showOnGeoDashboard !== undefined
        ? !!model.showOnGeoDashboard
        : false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setFormValues({
      cost_per_million_input_tokens: model.cost_per_million_input_tokens ?? "",
      cost_per_million_output_tokens:
        model.cost_per_million_output_tokens ?? "",
      public: model.public !== undefined ? !!model.public : true,
      showOnGeoDashboard:
        model.showOnGeoDashboard !== undefined
          ? !!model.showOnGeoDashboard
          : false,
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        cost_per_million_input_tokens:
          formValues.cost_per_million_input_tokens === ""
            ? null
            : Number(formValues.cost_per_million_input_tokens),
        cost_per_million_output_tokens:
          formValues.cost_per_million_output_tokens === ""
            ? null
            : Number(formValues.cost_per_million_output_tokens),
        public: !!formValues.public,
        showOnGeoDashboard: !!formValues.showOnGeoDashboard,
      };

      const response = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.admin.models}/${model._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (data.success) {
        onToast && onToast("Model updated successfully", "success");
        if (data.data) {
          onModelUpdated && onModelUpdated(data.data);
        } else {
          onSaved && onSaved();
        }
        setIsEditing(false);
      } else {
        onToast && onToast(data.error || "Failed to update model", "error");
      }
    } catch (err) {
      console.error("Error updating model:", err);
      onToast && onToast("An error occurred while updating model", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleGeoVisibilityToggle = async (checked) => {
    setGeoToggleSaving(true);
    try {
      const response = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.admin.models}/${model._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ showOnGeoDashboard: !!checked }),
        }
      );
      const data = await response.json();
      if (data.success) {
        const updatedModelFromServer = data.data || {
          ...model,
          showOnGeoDashboard: !!checked,
        };
        setFormValues((prev) => ({
          ...prev,
          showOnGeoDashboard:
            updatedModelFromServer.showOnGeoDashboard === true,
        }));
        onToast &&
          onToast(
            `Model ${
              checked ? "enabled for" : "hidden from"
            } GEO dashboard`,
            "success"
          );
        onModelUpdated && onModelUpdated(updatedModelFromServer);
      } else {
        onToast &&
          onToast(data.error || "Failed to update GEO visibility", "error");
      }
    } catch (err) {
      console.error("Error toggling GEO visibility:", err);
      onToast &&
        onToast("An error occurred while updating GEO visibility", "error");
    } finally {
      setGeoToggleSaving(false);
    }
  };

  const renderCosts = () => {
    const inCost = model.cost_per_million_input_tokens;
    const outCost = model.cost_per_million_output_tokens;
    return (
      <div className="model-costs">
        <div>
          <strong>Input/1M:</strong>{" "}
          {inCost === undefined || inCost === null ? "—" : inCost}
        </div>
        <div>
          <strong>Output/1M:</strong>{" "}
          {outCost === undefined || outCost === null ? "—" : outCost}
        </div>
        <div>
          <strong>Visibility:</strong>{" "}
          {model.public === false ? "Private" : "Public"}
        </div>
        <div>
          <strong>GEO Dashboard:</strong>{" "}
          {model.showOnGeoDashboard === true ? "Shown" : "Hidden"}
        </div>
      </div>
    );
  };

  return (
    <div className="model-card">
      <div className="model-card-header">
        <div style={{ flexGrow: 1 }}>
          <div className="model-name-id">{`${model.name} (${model.id})`}</div>
          <div className="model-badges">
            {model.reasoning && (
              <div className="model-badge reasoning">Reasoning</div>
            )}
            <div
              className={`model-badge provider ${model.provider || "unknown"}`}
            >
              {getProviderLabel(model.provider || "unknown")}
            </div>
          </div>
        </div>
        <button
          className="delete-button small"
          onClick={onDelete}
          title="Delete model"
        >
          &times;
        </button>
      </div>

      {!isEditing ? (
        <div className="model-card-body">
          {renderCosts()}
          <div className="form-group-checkbox">
            <input
              type="checkbox"
              id={`geo_visible_${model._id}`}
              checked={!!formValues.showOnGeoDashboard}
              onChange={(e) => handleGeoVisibilityToggle(e.target.checked)}
              disabled={geoToggleSaving}
            />
            <label htmlFor={`geo_visible_${model._id}`}>
              Show on GEO dashboard
            </label>
          </div>
          <div className="model-card-actions">
            <button
              className="admin-submit-button"
              onClick={() => setIsEditing(true)}
            >
              Edit Model Settings
            </button>
          </div>
        </div>
      ) : (
        <div className="model-card-body">
          <div className="form-group">
            <label htmlFor={`cost_in_${model._id}`}>
              Cost per 1M Input Tokens
            </label>
            <input
              id={`cost_in_${model._id}`}
              name="cost_per_million_input_tokens"
              type="number"
              step="0.000001"
              min="0"
              value={formValues.cost_per_million_input_tokens}
              onChange={handleChange}
              placeholder="e.g. 5.00"
            />
          </div>
          <div className="form-group">
            <label htmlFor={`cost_out_${model._id}`}>
              Cost per 1M Output Tokens
            </label>
            <input
              id={`cost_out_${model._id}`}
              name="cost_per_million_output_tokens"
              type="number"
              step="0.000001"
              min="0"
              value={formValues.cost_per_million_output_tokens}
              onChange={handleChange}
              placeholder="e.g. 15.00"
            />
          </div>
          <div className="form-group-checkbox">
            <input
              type="checkbox"
              id={`public_${model._id}`}
              name="public"
              checked={!formValues.public}
              onChange={(e) =>
                setFormValues((prev) => ({
                  ...prev,
                  public: !e.target.checked,
                }))
              }
            />
            <label htmlFor={`public_${model._id}`}>Private</label>
          </div>
          <div className="form-group-checkbox">
            <input
              type="checkbox"
              id={`geo_edit_${model._id}`}
              name="showOnGeoDashboard"
              checked={!!formValues.showOnGeoDashboard}
              onChange={(e) =>
                setFormValues((prev) => ({
                  ...prev,
                  showOnGeoDashboard: e.target.checked,
                }))
              }
            />
            <label htmlFor={`geo_edit_${model._id}`}>
              Show on GEO dashboard
            </label>
          </div>
          <div className="model-card-actions">
            <button
              className="admin-submit-button"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              className="delete-button"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
